import { useState } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { mergeKeywords, parseKeywordInput } from "./globalExcludedKeywordsUtils";

export function GlobalExcludedKeywordsModal({
  initialKeywords,
  onSave,
  onCancel,
}: {
  initialKeywords: string[];
  onSave: (keywords: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [keywords, setKeywords] = useState(() => [...initialKeywords]);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const inputKeywords = parseKeywordInput(input);
  const canAdd = inputKeywords.length > 0 && !saving;

  const handleAdd = () => {
    if (!canAdd) return;

    setKeywords((current) => mergeKeywords(current, inputKeywords));
    setInput("");
  };

  const handleSave = async () => {
    if (saving) return;

    const keywordsToSave = mergeKeywords(keywords, inputKeywords);
    setSaving(true);
    setSaveError("");
    try {
      await onSave(keywordsToSave);
    } catch {
      setSaveError(t("pathSettings.globalKeywords.saveFailed"));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="global-excluded-keywords-title"
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-sm"
      >
        <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-4">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
          >
            <i className="ri-global-line text-base" style={{ color: "#830051" }}></i>
          </div>
          <h3 id="global-excluded-keywords-title" className="text-sm font-semibold text-gray-900">
            {t("pathSettings.globalKeywords.title")}
          </h3>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto px-6 py-5">
          {keywords.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {keywords.map((keyword) => (
                <span
                  key={keyword.toLowerCase()}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-100 bg-gray-50/70 px-2.5 py-1.5 font-mono text-xs text-gray-700"
                >
                  <span className="break-all">{keyword}</span>
                  <button
                    type="button"
                    aria-label={t("pathSettings.globalKeywords.remove", { keyword })}
                    disabled={saving}
                    onClick={() =>
                      setKeywords((current) => current.filter((item) => item !== keyword))
                    }
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-red-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <i className="ri-close-line text-sm leading-none"></i>
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">{t("pathSettings.globalKeywords.empty")}</p>
          )}

          <div className="flex items-center gap-2.5">
            <input
              type="text"
              aria-label={t("pathSettings.globalKeywords.inputLabel")}
              value={input}
              disabled={saving}
              autoFocus
              placeholder={t("pathSettings.globalKeywords.placeholder")}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                handleAdd();
              }}
              className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm outline-none transition-colors placeholder:text-gray-400 focus:border-gray-400 disabled:bg-gray-50"
            />
            <button
              type="button"
              disabled={!canAdd}
              onClick={handleAdd}
              className="inline-flex h-8 shrink-0 items-center rounded-md px-2.5 text-[11px] font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              style={{ backgroundColor: "#830051" }}
            >
              {t("pathSettings.globalKeywords.add")}
            </button>
          </div>

          {saveError && (
            <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              {saveError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="cursor-pointer whitespace-nowrap rounded-lg border border-gray-200 px-4 py-1.5 text-xs text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("pathSettings.globalKeywords.cancel")}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-1.5 text-xs text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            style={{ backgroundColor: "#830051" }}
          >
            {saving && <i className="ri-loader-4-line animate-spin"></i>}
            {t("pathSettings.globalKeywords.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GlobalExcludedKeywords({
  keywords,
  loading,
  loadError,
  onRetry,
  onSave,
}: {
  keywords: string[];
  loading: boolean;
  loadError: string;
  onRetry: () => void;
  onSave: (keywords: string[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  return (
    <>
      <section className="mb-5 overflow-hidden rounded-xl border border-gray-100 bg-white">
        <div className="flex items-center justify-between gap-4 border-b border-gray-100 bg-gray-50 px-5 py-3">
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 className="text-sm font-semibold text-gray-800">
              {t("pathSettings.globalKeywords.title")}
            </h3>
            <Tooltip title={t("pathSettings.globalKeywords.tooltip")} placement="top">
              <button
                type="button"
                aria-label={t("pathSettings.globalKeywords.tooltip")}
                className="flex h-4 w-4 cursor-help items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-white hover:text-gray-600"
              >
                <i className="ri-question-line text-sm leading-none"></i>
              </button>
            </Tooltip>
          </div>
          <button
            type="button"
            disabled={loading || !!loadError}
            onClick={() => setEditing(true)}
            className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
            style={{ color: "#830051" }}
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-edit-line text-xs"></i>
            </span>
            {t("pathSettings.globalKeywords.edit")}
          </button>
        </div>

        <div className="min-h-[52px] px-5 py-4">
          {loading ? (
            <div className="flex items-center gap-2 py-2 text-[11px] text-gray-400">
              <i className="ri-loader-4-line animate-spin"></i>
              {t("pathSettings.globalKeywords.loading")}
            </div>
          ) : loadError ? (
            <div className="flex items-center gap-3 py-2 text-[11px] text-red-500">
              <span>{loadError}</span>
              <button
                type="button"
                onClick={onRetry}
                className="font-medium underline underline-offset-2 hover:text-red-600"
              >
                {t("pathSettings.globalKeywords.retry")}
              </button>
            </div>
          ) : keywords.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {keywords.map((keyword) => (
                <span
                  key={keyword.toLowerCase()}
                  className="rounded-md border border-gray-100 bg-gray-50/70 px-2.5 py-1.5 font-mono text-xs text-gray-700"
                >
                  {keyword}
                </span>
              ))}
            </div>
          ) : (
            <p className="py-2 text-[11px] text-gray-400">
              {t("pathSettings.globalKeywords.empty")}
            </p>
          )}
        </div>
      </section>

      {editing && (
        <GlobalExcludedKeywordsModal
          initialKeywords={keywords}
          onSave={async (nextKeywords) => {
            await onSave(nextKeywords);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </>
  );
}
